// In-app activity (DECISIONS "Activity and personalisation"): what someone
// does inside Guildite, used only for their own "For you" feed, recently
// viewed, search and sellers' totals. Nothing from outside the app.

export const ACTIVITY_TYPES = [
  'view', 'search', 'tap', 'like', 'save', 'share', 'follow', 'add_to_cart', 'purchase', 'guild_message', 'notification_open',
] as const;
export type ActivityType = typeof ACTIVITY_TYPES[number];
export const TARGET_KINDS = ['listing', 'post', 'shop', 'guild', 'person'] as const;
export type TargetKind = typeof TARGET_KINDS[number];
export type ActivityTarget = { kind: TargetKind; id: string };

export type ActivityEvent = {
  type: ActivityType;
  at: string;
  target: ActivityTarget | null;
  // view: seconds on screen (capped); search: the words; tap: what was tapped.
  seconds?: number;
  query?: string;
  what?: string;
  orderId?: string;
  // The kind of notification opened (never its text).
  notification?: string;
};

export const MAX_EVENTS_PER_BATCH = 50;
export const MAX_VIEW_SECONDS = 300;
const ID = /^[A-Za-z0-9_:.-]{1,128}$/;
const TAPS = new Set(['contact', 'whatsapp', 'call', 'directions', 'tagged_listing', 'place_tag', 'shop_link']);

function target(raw: unknown): ActivityTarget | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const kind = TARGET_KINDS.includes(value.kind as TargetKind) ? value.kind as TargetKind : null;
  const id = String(value.id ?? '');
  return kind && ID.test(id) ? { kind, id } : null;
}

// Events the app sent, cleaned: unknown types and fields are dropped, times
// must be within the last day (and not in the future), at most 50.
export function parseActivity(raw: unknown, now: number): ActivityEvent[] {
  if (!Array.isArray(raw)) return [];
  const events: ActivityEvent[] = [];
  for (const item of raw.slice(0, MAX_EVENTS_PER_BATCH)) {
    if (!item || typeof item !== 'object') continue;
    const value = item as Record<string, unknown>;
    const type = ACTIVITY_TYPES.includes(value.type as ActivityType) ? value.type as ActivityType : null;
    const time = Date.parse(String(value.at ?? ''));
    if (!type || !Number.isFinite(time) || time > now + 60_000 || time < now - 24 * 3600_000) continue;
    const event: ActivityEvent = { type, at: new Date(time).toISOString(), target: target(value.target) };
    if (type === 'view') {
      if (!event.target) continue;
      event.seconds = Math.max(0, Math.min(MAX_VIEW_SECONDS, Math.round(Number(value.seconds) || 0)));
    } else if (type === 'search') {
      const query = String(value.query ?? '').replace(/\s+/g, ' ').trim().slice(0, 80);
      if (query.length < 2) continue;
      event.query = query;
    } else if (type === 'tap') {
      const what = String(value.what ?? '');
      if (!TAPS.has(what) || !event.target) continue;
      event.what = what;
    } else if (type === 'purchase') {
      const orderId = String(value.orderId ?? '');
      if (!ID.test(orderId)) continue;
      event.orderId = orderId;
    } else if (type === 'notification_open') {
      event.notification = String(value.notification ?? '').replace(/[^a-z_]/g, '').slice(0, 40) || 'other';
    } else if (!event.target) {
      continue;
    }
    events.push(event);
  }
  return events;
}

// A safe key for a category or author inside a stored map.
export function interestKey(value: string): string {
  return value.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
}

export type Interests = Record<string, number>;
export type ActivityProfile = {
  // Category scores ("food", "phones"), from listings you look at and act on.
  interests: Interests;
  // Shops and people whose posts you engage with ("shop_seller_1").
  authors: Interests;
  recentlyViewed: { kind: 'listing' | 'shop'; id: string; at: string }[];
  recentSearches: { query: string; at: string }[];
  updatedAt: string | null;
};

export const EMPTY_PROFILE: ActivityProfile = { interests: {}, authors: {}, recentlyViewed: [], recentSearches: [], updatedAt: null };

const WEIGHTS: Record<ActivityType, number> = {
  view: 1, search: 0, tap: 2, like: 3, save: 4, share: 4, follow: 5, add_to_cart: 6, purchase: 8, guild_message: 1, notification_open: 0,
};

export function eventWeight(event: ActivityEvent): number {
  if (event.type === 'view') return 0.5 + Math.min(event.seconds ?? 0, 120) / 40;
  return WEIGHTS[event.type];
}

const HALF_LIFE_DAYS = 30;

function decayed(scores: Interests, days: number, keep: number): Interests {
  const factor = days > 0 ? 0.5 ** (days / HALF_LIFE_DAYS) : 1;
  return Object.fromEntries(Object.entries(scores)
    .map(([key, score]) => [key, Math.round(score * factor * 100) / 100] as const)
    .filter(([, score]) => score >= 0.05)
    .sort((left, right) => right[1] - left[1])
    .slice(0, keep));
}

// The profile after a batch: older scores fade (half every 30 days), then
// each event adds its weight to the listing's category and the author.
// categoryOf / authorOf come from records the server read, not the app.
export function applyActivity(profile: ActivityProfile, events: ActivityEvent[], context: {
  now: number; categoryOf: (target: ActivityTarget) => string | null; authorOf: (target: ActivityTarget) => string | null;
}): ActivityProfile {
  const days = profile.updatedAt ? Math.max(0, (context.now - Date.parse(profile.updatedAt)) / 86_400_000) : 0;
  const interests = decayed(profile.interests, days, 30);
  const authors = decayed(profile.authors, days, 50);
  let recentlyViewed = [...profile.recentlyViewed];
  let recentSearches = [...profile.recentSearches];
  for (const event of events) {
    const weight = eventWeight(event);
    if (event.target && weight) {
      const category = context.categoryOf(event.target);
      if (category) interests[category] = Math.round(((interests[category] ?? 0) + weight) * 100) / 100;
      const author = context.authorOf(event.target);
      if (author) authors[author] = Math.round(((authors[author] ?? 0) + weight) * 100) / 100;
    }
    if (event.type === 'view' && event.target && (event.target.kind === 'listing' || event.target.kind === 'shop')) {
      const seen = { kind: event.target.kind, id: event.target.id, at: event.at };
      recentlyViewed = [seen, ...recentlyViewed.filter(item => !(item.kind === seen.kind && item.id === seen.id))].slice(0, 30);
    }
    if (event.type === 'search' && event.query) {
      const query = event.query;
      recentSearches = [{ query, at: event.at }, ...recentSearches.filter(item => item.query.toLowerCase() !== query.toLowerCase())].slice(0, 20);
    }
  }
  return { interests: decayed(interests, 0, 30), authors: decayed(authors, 0, 50), recentlyViewed, recentSearches, updatedAt: new Date(context.now).toISOString() };
}

// "For you": interests and authors you engage with, your town, how new the
// post is and a little for popularity. Ties keep the newest first.
export function rankForYou<T extends { createdAt: string; authorKey: string; categories: string[]; likeCount: number; local: boolean }>(items: T[], profile: ActivityProfile, now: number): T[] {
  const score = (item: T) => {
    const ageHours = Math.max(0, (now - Date.parse(item.createdAt)) / 3_600_000);
    const freshness = 3 / (1 + ageHours / 24);
    const interest = Math.max(0, ...item.categories.map(category => profile.interests[interestKey(category)] ?? 0));
    const author = profile.authors[interestKey(item.authorKey)] ?? 0;
    return freshness + Math.log1p(interest) * 1.5 + Math.log1p(author) * 2 + (item.local ? 1 : 0) + Math.log1p(item.likeCount) * 0.3;
  };
  return items.map(item => ({ item, value: score(item) }))
    .sort((left, right) => right.value - left.value || right.item.createdAt.localeCompare(left.item.createdAt))
    .map(entry => entry.item);
}

// Listings in categories you like first; keeps the order inside equal scores.
export function rankByInterest<T extends { category: string }>(items: T[], profile: ActivityProfile): T[] {
  return items.map((item, index) => ({ item, index, value: profile.interests[interestKey(item.category)] ?? 0 }))
    .sort((left, right) => right.value - left.value || left.index - right.index)
    .map(entry => entry.item);
}

// Seller insights show a town only when at least this many views came from it,
// so no single buyer can be picked out.
export const INSIGHT_MIN_COUNT = 5;
